import React from "react";
import { Box } from "@chakra-ui/react";
import { SectionComponent } from "newui/features/Rfx/Submission/components/ReturnableSchedule/SectionComponent";
import { fieldTypeQuestion } from "helpers/Enum/FieldTypeList";
import { useSelector } from "react-redux";
import { useFormikContext, FormikValues } from "formik";
import { RfxReturnableSchedule } from "../../../types/returnable-schedule-question.type";
import { RfxAnswers } from "../../types/returnable-schedule-answer.type";
import {
	filterFieldValues,
	getAnswerForQuestion,
} from "../../submission-helpers";
import { hasDocumentsForCategories } from "pages/RFQSubmission/RFQPreview/RFQUtils/RFQHelper";
import { renderQuestion } from "./QuestionComponent";
import ReturnableSchedulesFileUploadComp from "pages/RFQSubmission/RFQPreview/ReturnableSchedulesFileUploadComp";
import { QUESTION_TYPE_ID } from "../../constants";
import { AdditionalComment } from "./QuestionComponent/_components/AdditionalComment";
import { TextLength } from "newui/features/Rfx/types/component-types";
import { CreateQuestionSection } from "newui/features/Rfx/types/Creation/create-rfx-form-types";
import { UploadFile } from "types/storage.types";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { RootState } from "newui/store";
import { useCurrentCompany } from "hooks/auth";

interface ReturnableSchedulesProps {
	companyName?: string;
	disabled?: boolean;
	isPreview?: boolean;
	isReturnableSchedulesTypeFile?: "file" | string;
	returnableSchedule?: RfxReturnableSchedule | CreateQuestionSection[];
	rfxAnswers: RfxAnswers;
	rfxInviteId: number;
	templateReturnableScheduleFile?: UploadFile;
	touchedForm?: Record<string, any>;
}

export const ReturnableSchedule: React.FC<ReturnableSchedulesProps> = ({
	returnableSchedule,
	rfxAnswers,
	touchedForm,
	isReturnableSchedulesTypeFile,
	companyName,
	templateReturnableScheduleFile,
	disabled = false,
	rfxInviteId,
	isPreview = false,
}) => {
	const { rfxId, rfxSubmissionData } = useRfxSubmissionData();
	const currentCompany = useCurrentCompany();
	const { documents } = useSelector(
		(state: RootState) => state.companyProfile
	);

	const formik = useFormikContext<FormikValues>();
	const hasResources = currentCompany?.has_key_personnel ?? false;
	const hasPastProjects = currentCompany?.has_past_projects ?? false;

	const hasErrorForQuestion = (questionId: number): boolean => {
		const keyBase = formik?.errors?.question_answer;

		if (!keyBase || !questionId) {
			return false;
		}

		const keys = Object.keys(keyBase);
		return keys.some(
			(key) =>
				key === `question-${questionId}` ||
				key.startsWith(`question-${questionId}-`) // catches comments and array fields
		);
	};

	const hasErrorInSection = (section) =>
		section.questionnaire?.some((q) => hasErrorForQuestion(q.id));

	return (
		<div className="space-y-10">
			{isReturnableSchedulesTypeFile === "file" ? (
				<ReturnableSchedulesFileUploadComp
					companyName={companyName}
					templateReturnableScheduleFile={templateReturnableScheduleFile}
					rfqId={rfxId}
					rfxInviteId={rfxInviteId}
					disabled={disabled}
					isPreview={isPreview}
				/>
			) : (
				returnableSchedule?.map((section, sectionID) => {
					const sectionHasError = hasErrorInSection(section);

					return (
						<Box
							key={sectionID}
							className={`block min-h-80 w-full space-y-8 rounded-xl border p-10 pt-4 shadow-xl`}
						>
							<SectionComponent
								section={section}
								isSubmission={true}
								hasError={sectionHasError}
							/>

							{section.questionnaire?.map((question, index) => {
								const answer = getAnswerForQuestion(question, rfxAnswers);
								const nameField = `question_answer.question-${question.id}`;
								const questionHasError = hasErrorForQuestion(question.id);

								const questionProps = {
									type: question.type,
									name: nameField,
									question: question.name,
									fieldValue: filterFieldValues(question) || [],
									description: fieldTypeQuestion[question.type]?.description,
									rfqSubmission: true,
									required: question.required,
									touchedField: touchedForm,
									disabled: disabled,
									answer: answer,
									formik: formik,
									hasError: questionHasError, // optional if your renderQuestion supports it
								};

								const hasDocuments =
									question.type === QUESTION_TYPE_ID.CORPORATE_DOCUMENTS
										? hasDocumentsForCategories(question, documents)
										: false;

								return (
									<Box
										key={question.id}
										rounded="md"
										borderWidth="1px"
										padding={2}
										className={questionHasError ? "border-red-500" : ""}
									>
										{renderQuestion(
											questionProps,
											index,
											hasDocuments,
											hasResources,
											hasPastProjects,
											disabled,
											rfxSubmissionData,
											isPreview,
											question
										)}

										{(question.hasComment === 1 ||
											question.hasComment === true) && (
												<AdditionalComment
													key={`${question.id}-comment`}
													textLength={TextLength.SHORT}
													questionName={nameField}
													isReadOnly={disabled}
													isClientPreview={isPreview}
													{...question}
												/>
											)}
									</Box>
								);
							})}
						</Box>
					);
				})
			)}
		</div>
	);
};
